import type { SecondEarnerDay, SecondEarnerOutput } from "./types.js";

// Plain-English result copy for the second-earner surface. Lives in the rules
// package so every channel (Expo result screen, "my results" list, PDF report)
// renders identical wording from one source — which matters for REGULATION, not
// just DRY. Every string here is INFORMATION, not advice (validation §3):
//
//   • GROSS is always shown beside NET (binding ship-condition).
//   • The per-day waterfall is described as NEUTRAL MECHANICS — what tax, FTB-A
//     withdrawal and CCS step-down strip from that day — each cited to its
//     ATO / Services Australia source.
//   • Figures are framed as ESTIMATES; "Services Australia / the ATO finalises".
//   • HARD RULE: NO copy resolves to "worth it / not worth working / stay home /
//     work more". There is no headline that scores the decision. The keep-rate
//     is described as "what you keep of that day", a neutral mechanic.
//
// Pure string-builders, unit-tested.

/** Whole-dollar AUD with thousands separators. */
export function formatAud(amount: number): string {
  return `A$${Math.round(amount).toLocaleString("en-AU")}`;
}

/** A fraction rendered as a whole-or-one-dp percent (e.g. 0.62 → "62%"). */
export function formatPct(fraction: number): string {
  return `${+(fraction * 100).toFixed(1)}%`;
}

/** Mandatory across every second-earner surface (validation §3.1). */
export const DISCLAIMER = "Educational tool. Not financial advice.";

/**
 * Neutral headline: states GROSS beside NET across the modelled days. It does
 * NOT score the decision — there is deliberately no "worth it" framing.
 */
export function headline(out: SecondEarnerOutput): string {
  const n = out.days.length;
  return (
    `Across ${n} extra ${n === 1 ? "day" : "days"}: ${formatAud(out.totalGross)} gross, ` +
    `${formatAud(out.totalNet)} net after tax, Family Tax Benefit Part A withdrawal and ` +
    `out-of-pocket childcare. These are estimates; Services Australia and the ATO finalise the figures.`
  );
}

/**
 * The per-day waterfall line — neutral mechanics, gross beside net, each
 * reduction cited to its source. Never a verdict.
 */
export function dayBullet(d: SecondEarnerDay): string {
  const parts: string[] = [];
  parts.push(`Day ${d.day}: ${formatAud(d.gross)} gross`);
  if (d.extraTax > 0) parts.push(`− ${formatAud(d.extraTax)} income tax + Medicare levy (ATO)`);
  if (d.extraHelp > 0) parts.push(`− ${formatAud(d.extraHelp)} study-loan repayment (ATO)`);
  if (d.ftbWithdrawn > 0)
    parts.push(`− ${formatAud(d.ftbWithdrawn)} Family Tax Benefit Part A withdrawn (Services Australia income test)`);
  if (d.extraChildcare > 0)
    parts.push(
      `− ${formatAud(d.extraChildcare)} extra childcare out-of-pocket as your Child Care Subsidy steps down (Services Australia)`,
    );
  const keep =
    d.keepRate === null
      ? ""
      : ` You keep ${formatAud(d.net)} of this day — ${formatPct(d.keepRate)} of the gross.`;
  return `${parts.join(" ")} = ${formatAud(d.net)} net.${keep}`;
}

export function dayBullets(out: SecondEarnerOutput): string[] {
  return out.days.map(dayBullet);
}

/** A neutral note on WHY the keep-rate falls on later days — mechanics, not a
 * recommendation. Cites the Treasury framing as context only. */
export function mechanicsNote(): string {
  return (
    `As your family's combined income rises with each extra day, more of that day is taxed at your ` +
    `marginal rate, more Family Tax Benefit Part A is withdrawn, and your Child Care Subsidy percentage ` +
    `steps down so the same care costs more out of pocket. This is why later days typically keep a ` +
    `smaller share than the first — the same pattern Treasury describes as the workforce disincentive ` +
    `rate. The figures above show how it falls for the numbers you entered.`
  );
}
